import OpenAI from 'openai';

const openai = new OpenAI({
  apiKey: process.env.REACT_APP_OPENAI_API_KEY,
  dangerouslyAllowBrowser: true
});

export const generateInsights = async (reviews) => {
  try {
    const reviewSample = reviews.slice(0, 50).map(review => 
      `${review.DATE} | ${review.SEGMENT} | ${review.RATING}/5 | ${review.REVIEW}`
    ).join('\n');

    const response = await openai.chat.completions.create({
      model: "gpt-4o-mini",
      messages: [{
        role: "system",
        content: "You are an expert data analyst specializing in customer experience for hospitality businesses. Focus on enhancing value perception, guest experience improvements, and operational excellence rather than price reductions. Provide specific percentages and numerical breakdowns in sentiment analysis."
      }, {
        role: "user", 
        content: `Analyze these Camelback Resort reviews and provide insights focused on value enhancement rather than price reductions. Calculate specific percentages for sentiment analysis and provide data-driven recommendations for improving guest experience and perceived value. Return ONLY valid JSON without any additional text or explanation.

IMPORTANT GUIDANCE:
- Focus recommendations on enhancing value perception, service quality, and guest experience
- Avoid suggesting price reductions - instead suggest ways to increase perceived value
- Include specific percentages for positive/neutral/negative sentiment 
- Provide numerical averages for ratings by segment
- Make recommendations actionable and focused on operational improvements

${reviewSample}

Return insights in this exact JSON structure:
{
  "sentimentTrends": {
    "overall": "positive/negative/mixed",
    "positivePercentage": 75,
    "neutralPercentage": 15,
    "negativePercentage": 10,
    "averageRating": 4.2,
    "totalReviews": 1250,
    "bySegment": {
      "General Hotel": "positive/negative/mixed",
      "Ski Mountain": "positive/negative/mixed", 
      "Aquatopia": "positive/negative/mixed",
      "Camelbeach": "positive/negative/mixed",
      "Mountain Adventures": "positive/negative/mixed"
    },
    "segmentBreakdown": {
      "General Hotel": {"positive": 80, "neutral": 15, "negative": 5, "avgRating": 4.3},
      "Ski Mountain": {"positive": 75, "neutral": 20, "negative": 5, "avgRating": 4.1},
      "Aquatopia": {"positive": 85, "neutral": 10, "negative": 5, "avgRating": 4.4},
      "Camelbeach": {"positive": 70, "neutral": 20, "negative": 10, "avgRating": 3.9},
      "Mountain Adventures": {"positive": 78, "neutral": 18, "negative": 4, "avgRating": 4.2}
    }
  },
  "keyThemes": [
    {"theme": "theme name", "sentiment": "positive/negative", "frequency": "high/medium/low", "segments": ["segment1", "segment2"]},
  ],
  "commonComplaints": [
    {"issue": "complaint description", "segments": ["segment1"], "frequency": "high/medium/low"}
  ],
  "commonCompliments": [
    {"praise": "compliment description", "segments": ["segment1"], "frequency": "high/medium/low"}
  ],
  "seasonalPatterns": [
    {"pattern": "pattern description", "timeframe": "season/month", "impact": "positive/negative"}
  ],
  "actionableRecommendations": [
    {"recommendation": "specific action to enhance value perception and guest experience", "priority": "high/medium/low", "segments": ["segment1"], "focus": "value-enhancement"}
  ]
}`
      }],
      temperature: 0.3,
      max_tokens: 1500
    });

    const content = response.choices[0].message.content;
    console.log('AI Response:', content);
    
    // Try to extract JSON from the response if it contains extra text
    let jsonString = content;
    
    // Look for JSON object boundaries
    const jsonStart = content.indexOf('{');
    const jsonEnd = content.lastIndexOf('}') + 1;
    
    if (jsonStart !== -1 && jsonEnd > jsonStart) {
      jsonString = content.substring(jsonStart, jsonEnd);
    }
    
    try {
      return JSON.parse(jsonString);
    } catch (parseError) {
      console.error('JSON Parse Error:', parseError);
      console.error('Raw content:', content);
      console.error('Attempted to parse:', jsonString);
      throw new Error('AI returned invalid JSON format');
    }
  } catch (error) {
    console.error('Error generating insights:', error);
    if (error.message?.includes('API key')) {
      throw new Error('OpenAI API key issue. Please check your configuration.');
    }
    if (error.message?.includes('rate limit')) {
      throw new Error('Rate limit exceeded. Please try again in a moment.');
    }
    throw new Error(`Failed to generate AI insights: ${error.message}`);
  }
};

export const processNaturalLanguageQuery = async (query, reviews, reviewContext = null) => {
  try {
    const reviewSample = reviews.slice(0, 30).map(review => 
      `${review.DATE} | ${review.SEGMENT} | ${review.RATING}/5 | ${review.REVIEW}`
    ).join('\n');

    // Generate context information
    const contextInfo = reviewContext ? `
Analysis Context:
- **Total Reviews**: ${reviewContext.totalReviews.toLocaleString()}
- **Date Range**: ${reviewContext.dateRange?.earliest} to ${reviewContext.dateRange?.latest}
- **Segment Breakdown**: ${Object.entries(reviewContext.segments).map(([segment, count]) => `${segment} (${count.toLocaleString()})`).join(', ')}
` : '';

    const response = await openai.chat.completions.create({
      model: "gpt-4o-mini",
      messages: [{
        role: "system",
        content: `You are Clairity, an AI assistant for Camelback Resort that analyzes customer reviews. 

IMPORTANT FORMATTING RULES:
- Always format your response in Markdown
- Use **bold** for key insights and numbers
- Use bullet points (-) for lists
- Use ## for section headers when appropriate
- Always include specific review counts in your analysis (e.g., "Based on 48 reviews from Aquatopia...")
- Cite specific segments, dates, and numbers from the data
- Make your responses conversational but data-driven

Business segments: General Hotel, Ski Mountain, Aquatopia, Camelbeach, Mountain Adventures

Provide specific, data-driven answers with clear formatting and exact counts where possible.`
      }, {
        role: "user",
        content: `${contextInfo}

Based on these Camelback Resort reviews, please answer this question: "${query}"

Review data sample (30 most recent):
${reviewSample}

Please provide a comprehensive markdown-formatted answer with specific examples, counts, and insights. Always mention how many reviews you're analyzing and from which segments/time periods.`
      }],
      temperature: 0.4,
      max_tokens: 1000
    });

    return response.choices[0].message.content;
  } catch (error) {
    console.error('Error processing natural language query:', error);
    throw new Error('Failed to process your question');
  }
};

export const generateReviewSummary = async (reviews, filterType = 'all') => {
  try {
    let contextMessage = '';
    switch (filterType) {
      case 'segment':
        contextMessage = `Summarize reviews for the ${reviews[0]?.SEGMENT} segment`;
        break;
      case 'rating':
        contextMessage = `Summarize ${reviews[0]?.RATING}-star reviews`;
        break;
      case 'timeframe':
        contextMessage = `Summarize reviews for the selected time period`;
        break;
      default:
        contextMessage = 'Summarize all reviews';
    }

    const reviewText = reviews.slice(0, 40).map(review => 
      `${review.DATE} | ${review.SEGMENT} | ${review.RATING}/5 | ${review.REVIEW}`
    ).join('\n');

    const response = await openai.chat.completions.create({
      model: "gpt-4o-mini",
      messages: [{
        role: "system",
        content: "You are a customer experience analyst. Create concise, actionable summaries of customer reviews for hospitality management."
      }, {
        role: "user",
        content: `${contextMessage} based on this data. Return ONLY valid JSON without any additional text or explanation.

${reviewText}

Provide a summary in this exact JSON format:
{
  "overallSentiment": "positive/negative/mixed",
  "keyFindings": ["finding 1", "finding 2", "finding 3"],
  "strengths": ["strength 1", "strength 2"],
  "areasForImprovement": ["improvement 1", "improvement 2"],
  "totalReviews": ${reviews.length},
  "averageRating": number,
  "summary": "2-3 sentence executive summary"
}`
      }],
      temperature: 0.3,
      max_tokens: 600
    });

    const content = response.choices[0].message.content;
    console.log('Summary AI Response:', content);
    
    // Try to extract JSON from the response if it contains extra text
    let jsonString = content;
    
    // Look for JSON object boundaries
    const jsonStart = content.indexOf('{');
    const jsonEnd = content.lastIndexOf('}') + 1;
    
    if (jsonStart !== -1 && jsonEnd > jsonStart) {
      jsonString = content.substring(jsonStart, jsonEnd);
    }
    
    try {
      return JSON.parse(jsonString);
    } catch (parseError) {
      console.error('Summary JSON Parse Error:', parseError);
      console.error('Raw summary content:', content);
      console.error('Attempted to parse:', jsonString);
      throw new Error('AI returned invalid JSON format for summary');
    }
  } catch (error) {
    console.error('Error generating summary:', error);
    if (error.message?.includes('API key')) {
      throw new Error('OpenAI API key issue. Please check your configuration.');
    }
    if (error.message?.includes('rate limit')) {
      throw new Error('Rate limit exceeded. Please try again in a moment.');
    }
    throw new Error(`Failed to generate review summary: ${error.message}`);
  }
};

export const analyzeTrends = async (reviews) => {
  try {
    // Group reviews by month and segment for trend analysis
    const monthlyData = {};
    reviews.forEach(review => {
      const month = new Date(review.DATE).toISOString().slice(0, 7); // YYYY-MM format
      if (!monthlyData[month]) {
        monthlyData[month] = {};
      }
      if (!monthlyData[month][review.SEGMENT]) {
        monthlyData[month][review.SEGMENT] = { ratings: [], count: 0 };
      }
      monthlyData[month][review.SEGMENT].ratings.push(review.RATING);
      monthlyData[month][review.SEGMENT].count++;
    });

    const trendData = Object.entries(monthlyData).map(([month, segments]) => {
      const entry = { month };
      Object.entries(segments).forEach(([segment, data]) => {
        const avgRating = data.ratings.reduce((a, b) => a + b, 0) / data.ratings.length;
        entry[segment] = parseFloat(avgRating.toFixed(2));
        entry[`${segment}_count`] = data.count;
      });
      return entry;
    }).sort((a, b) => a.month.localeCompare(b.month));

    // Generate AI analysis of trends
    const trendSummary = trendData.slice(-6).map(entry => 
      `${entry.month}: ${Object.entries(entry)
        .filter(([key]) => !key.includes('_count') && key !== 'month')
        .map(([segment, rating]) => `${segment}: ${rating}/5`)
        .join(', ')}`
    ).join('\n');

    const response = await openai.chat.completions.create({
      model: "gpt-4o-mini",
      messages: [{
        role: "system",
        content: "You are a business intelligence analyst specializing in hospitality trends. Identify patterns, seasonal effects, and emerging issues or improvements."
      }, {
        role: "user",
        content: `Analyze these rating trends for Camelback Resort. Return ONLY valid JSON without any additional text or explanation.

${trendSummary}

Provide analysis in this exact JSON format:
{
  "trendAnalysis": [
    {"segment": "segment name", "trend": "improving/declining/stable", "change": "+/-X.X points", "insight": "explanation"}
  ],
  "seasonalPatterns": [
    {"pattern": "description", "segments": ["affected segments"], "recommendation": "action item"}
  ],
  "emergingIssues": [
    {"issue": "problem description", "segments": ["affected segments"], "severity": "high/medium/low"}
  ],
  "improvements": [
    {"improvement": "positive change", "segments": ["affected segments"], "impact": "description"}
  ]
}`
      }],
      temperature: 0.4,
      max_tokens: 800
    });

    const content = response.choices[0].message.content;
    console.log('Trends AI Response:', content);
    
    // Try to extract JSON from the response if it contains extra text
    let jsonString = content;
    
    // Look for JSON object boundaries
    const jsonStart = content.indexOf('{');
    const jsonEnd = content.lastIndexOf('}') + 1;
    
    if (jsonStart !== -1 && jsonEnd > jsonStart) {
      jsonString = content.substring(jsonStart, jsonEnd);
    }
    
    let analysis;
    try {
      analysis = JSON.parse(jsonString);
    } catch (parseError) {
      console.error('Trends JSON Parse Error:', parseError);
      console.error('Raw trends content:', content);
      console.error('Attempted to parse:', jsonString);
      throw new Error('AI returned invalid JSON format for trends analysis');
    }

    return {
      chartData: trendData,
      analysis: analysis
    };
  } catch (error) {
    console.error('Error analyzing trends:', error);
    if (error.message?.includes('API key')) {
      throw new Error('OpenAI API key issue. Please check your configuration.');
    }
    if (error.message?.includes('rate limit')) {
      throw new Error('Rate limit exceeded. Please try again in a moment.');
    }
    throw new Error(`Failed to analyze trends: ${error.message}`);
  }
};

export const extractEmployeeMentions = async (reviews) => {
  try {
    // Reviews are already filtered by the component for timeframe, segment, and positive ratings
    if (reviews.length === 0) {
      return { employeeData: [], monthlyData: [] };
    }

    const reviewSample = reviews.slice(0, 100).map(review => 
      `${review.DATE} | ${review.SEGMENT} | ${review.RATING}/5 | ${review.REVIEW}`
    ).join('\n');

    const response = await openai.chat.completions.create({
      model: "gpt-4o-mini",
      messages: [{
        role: "system",
        content: "You are an expert at identifying employee mentions in hospitality reviews. Extract employee names, roles, and positive actions mentioned in guest reviews. Only include clear employee mentions with specific names or job titles.\n\nIMPORTANT: Pay attention to name variations - guests may spell employee names phonetically or with slight variations. Group similar names together (e.g., 'Mike', 'Michael', 'Mick' or 'Katherine', 'Catherine', 'Kate', 'Katie'). Also account for common misspellings and different pronunciations of the same name. When in doubt, use the most complete/formal version of the name you can determine."
      }, {
        role: "user",
        content: `Extract employee mentions from these positive Camelback Resort reviews. Return ONLY valid JSON without any additional text or explanation.

IMPORTANT: When you find employee names, normalize similar names to avoid duplicates. For example:
- "Mike", "Michael", "Mick" should all be recorded as "Michael"  
- "Katie", "Kate", "Katherine" should all be recorded as "Katherine"
- Account for common misspellings like "Jhon" → "John", "Kristen" → "Kristin"
- Use the most complete/formal version of the name when possible

${reviewSample}

Return results in this exact JSON format:
{
  "employees": [
    {
      "name": "employee name or role",
      "segment": "business segment",
      "month": "YYYY-MM",
      "date": "YYYY-MM-DD",
      "mentions": 1,
      "positiveActions": ["action 1", "action 2"],
      "reviewExcerpt": "brief quote from review"
    }
  ]
}`
      }],
      temperature: 0.2,
      max_tokens: 2000
    });

    const content = response.choices[0].message.content;
    console.log('Employee Mentions AI Response:', content);
    
    // Try to extract JSON from the response if it contains extra text
    let jsonString = content;
    
    // Look for JSON object boundaries
    const jsonStart = content.indexOf('{');
    const jsonEnd = content.lastIndexOf('}') + 1;
    
    if (jsonStart !== -1 && jsonEnd > jsonStart) {
      jsonString = content.substring(jsonStart, jsonEnd);
    }
    
    let extractedData;
    try {
      extractedData = JSON.parse(jsonString);
    } catch (parseError) {
      console.error('Employee Mentions JSON Parse Error:', parseError);
      console.error('Raw employee content:', content);
      console.error('Attempted to parse:', jsonString);
      throw new Error('AI returned invalid JSON format for employee mentions');
    }

    // Process the data into monthly performance metrics
    const employeeStats = {};
    const monthlyData = {};

    extractedData.employees?.forEach(emp => {
      const key = `${emp.name}_${emp.segment}`;
      
      if (!employeeStats[key]) {
        employeeStats[key] = {
          name: emp.name,
          segment: emp.segment,
          totalMentions: 0,
          monthlyMentions: {},
          positiveActions: new Set(),
          reviews: []
        };
      }
      
      employeeStats[key].totalMentions += emp.mentions || 1;
      employeeStats[key].monthlyMentions[emp.month] = (employeeStats[key].monthlyMentions[emp.month] || 0) + (emp.mentions || 1);
      
      // Store individual review mentions
      employeeStats[key].reviews.push({
        date: emp.date,
        excerpt: emp.reviewExcerpt,
        actions: emp.positiveActions || []
      });
      
      emp.positiveActions?.forEach(action => {
        employeeStats[key].positiveActions.add(action);
      });

      // Track monthly data for trends
      if (!monthlyData[emp.month]) {
        monthlyData[emp.month] = {};
      }
      if (!monthlyData[emp.month][emp.segment]) {
        monthlyData[emp.month][emp.segment] = 0;
      }
      monthlyData[emp.month][emp.segment] += emp.mentions || 1;
    });

    // Convert to array and sort by total mentions
    const employeeData = Object.values(employeeStats)
      .map(emp => ({
        ...emp,
        positiveActions: Array.from(emp.positiveActions),
        avgMonthlyMentions: emp.totalMentions / Math.max(Object.keys(emp.monthlyMentions).length, 1),
        reviews: emp.reviews.sort((a, b) => new Date(b.date) - new Date(a.date))
      }))
      .sort((a, b) => b.totalMentions - a.totalMentions);

    // Convert monthly data for charting
    const monthlyArray = Object.entries(monthlyData).map(([month, segments]) => ({
      month,
      ...segments
    })).sort((a, b) => a.month.localeCompare(b.month));

    return {
      employeeData,
      monthlyData: monthlyArray
    };

  } catch (error) {
    console.error('Error extracting employee mentions:', error);
    if (error.message?.includes('API key')) {
      throw new Error('OpenAI API key issue. Please check your configuration.');
    }
    if (error.message?.includes('rate limit')) {
      throw new Error('Rate limit exceeded. Please try again in a moment.');
    }
    throw new Error(`Failed to extract employee mentions: ${error.message}`);
  }
};